import {
  type ProgressStep,
  phaseToStage,
  STAGE_DESCRIPTIONS,
  STAGE_LABELS,
  type Stage,
} from '@aira/shared';
import { Button } from '@fluentui/react-components';
import { CheckCircle2, ChevronDown, Circle, Loader2, MinusCircle, X, XCircle } from 'lucide-react';
import type { ProgressGroup } from '../api/ragBackend';

type ProgressStepWithTiming = ProgressStep & { durationMs?: number | null };

function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function statusLabel(status: ProgressGroup['status']): string {
  if (status === 'completed') return 'Done';
  if (status === 'running') return 'Now';
  if (status === 'failed') return 'Failed';
  if (status === 'skipped') return 'Skipped';
  return 'Pending';
}

export function StageProgressDetails({
  stage,
  steps,
  groups = [],
  onClose,
}: {
  stage: Stage;
  steps: ProgressStepWithTiming[];
  groups?: ProgressGroup[];
  onClose: () => void;
}) {
  const stageSteps = steps.filter((step) => phaseToStage(step.phase) === stage);
  const stageGroups = groups.filter((group) =>
    group.phases.some((phase) => phaseToStage(phase) === stage),
  );
  const totalMs = stageGroups.reduce((sum, group) => sum + (group.durationMs ?? 0), 0);
  const totalTime = totalMs > 0 ? formatDuration(totalMs) : null;
  const resolvedGroupPhases = new Set(
    stageGroups
      .filter((group) => group.status === 'completed' || group.status === 'skipped')
      .flatMap((group) => group.phases),
  );
  const finishedStepCount = stageSteps.filter(
    (step) =>
      step.status === 'completed' ||
      step.status === 'failed' ||
      (step.status === 'pending' && resolvedGroupPhases.has(step.phase)),
  ).length;

  return (
    <section className="rounded-lg border border-sf-primary/15 bg-sf-surface shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-sf-primary/10 bg-sf-primary-light/55 px-3 py-2.5">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-sf-primary">
            {STAGE_LABELS[stage]} details
          </p>
          <p className="mt-0.5 text-xs leading-snug text-sf-gray">{STAGE_DESCRIPTIONS[stage]}</p>
          {totalTime && (
            <p className="mt-1 text-[11px] font-semibold tabular-nums text-sf-primary">
              Elapsed time: {totalTime}
            </p>
          )}
        </div>
        <Button
          appearance="subtle"
          size="small"
          icon={<X className="h-3.5 w-3.5" />}
          onClick={onClose}
          aria-label="Close progress details"
        />
      </div>
      {stageGroups.length > 0 && (
        <div className="grid gap-2 border-b border-sf-primary/10 px-3 py-3">
          {stageGroups.map((group) => {
            const time = formatDuration(group.durationMs);
            const failed = group.status === 'failed';
            const running = group.status === 'running';
            const done = group.status === 'completed';
            const badgeTone = failed
              ? 'bg-red-50 text-red-700 ring-red-200'
              : running
                ? 'bg-sf-primary text-sf-secondary ring-sf-primary'
                : done
                  ? 'bg-sf-primary-light text-sf-primary ring-sf-primary/20'
                  : 'bg-sf-surface text-sf-gray ring-sf-primary/15';

            return (
              <div
                key={group.key}
                className="flex items-center justify-between gap-3 rounded-md border border-sf-primary/10 bg-sf-primary-light/35 px-3 py-2"
              >
                <span className="min-w-0 text-xs font-semibold leading-snug text-sf-dark">
                  {group.label}
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${badgeTone}`}
                  >
                    {statusLabel(group.status)}
                  </span>
                  {time && (
                    <span className="rounded-full bg-sf-surface px-2 py-0.5 text-[10px] font-semibold tabular-nums text-sf-primary ring-1 ring-sf-primary/15">
                      {time}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {stageSteps.length ? (
        <details className="group/details">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-xs font-semibold text-sf-primary hover:bg-sf-primary-light/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sf-primary">
            <span className="min-w-0 flex-1">Detailed processing steps</span>
            <span className="shrink-0 text-[10px] font-medium tabular-nums text-sf-gray">
              {finishedStepCount}/{stageSteps.length}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open/details:rotate-180" />
          </summary>
          <ol className="divide-y divide-sf-primary/10 border-t border-sf-primary/10">
            {stageSteps.map((step) => {
              const done = step.status === 'completed';
              const failed = step.status === 'failed';
              const active = step.status === 'started';
              const skipped = step.status === 'pending' && resolvedGroupPhases.has(step.phase);
              const time = formatDuration(step.durationMs);
              const Icon = done
                ? CheckCircle2
                : failed
                  ? XCircle
                  : active
                    ? Loader2
                    : skipped
                      ? MinusCircle
                      : Circle;
              const tone = done
                ? 'text-sf-primary'
                : failed
                  ? 'text-red-600'
                  : active
                    ? 'text-sf-primary'
                    : skipped
                      ? 'text-sf-gray'
                      : 'text-sf-gray/60';

              return (
                <li key={step.phase} className="flex items-center gap-2 px-3 py-2.5">
                  <Icon className={`h-4 w-4 shrink-0 ${tone} ${active ? 'animate-spin' : ''}`} />
                  <span className={`min-w-0 text-xs leading-snug ${tone}`}>{step.label}</span>
                  {skipped && (
                    <span className="ml-auto shrink-0 rounded-full bg-sf-surface px-2 py-0.5 text-[10px] font-semibold text-sf-gray ring-1 ring-sf-primary/15">
                      Skipped
                    </span>
                  )}
                  {time && (
                    <span className="ml-auto shrink-0 rounded-full bg-sf-primary-light px-2 py-0.5 text-[10px] font-semibold tabular-nums text-sf-primary">
                      {time}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </details>
      ) : (
        <p className="px-3 py-3 text-xs text-sf-gray">No detailed progress for this stage yet.</p>
      )}
    </section>
  );
}
